import { useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import type { AuditEvent } from '@weave/schema-gen-ts/dist/apis/api/phone/audits/v1/api.pb';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { EventListItem, MARKER_SIZE, TIME_ROW_HEIGHT } from './event-list-item';

type EventDetailsListProps = {
  events: AuditEvent[];
  activeEventId?: string;
};

/**
 * Renders a vertical timeline list of audit events for the history details tray.
 *
 * A single continuous vertical line is drawn from the top of the first marker to the
 * center of the last marker. The line height is dynamically measured via ResizeObserver
 * so it stays correct when event cards expand/collapse. If there is only one event,
 * the line is hidden since there is nothing to connect.
 */
export const EventDetailsList = ({ events, activeEventId }: EventDetailsListProps) => {
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const listRef = useRef<HTMLUListElement>(null);
  const activeRef = useRef<HTMLLIElement>(null);
  const needsScroll = !!activeEventId;
  const [isPositioned, setIsPositioned] = useState(!needsScroll);
  const [lineHeight, setLineHeight] = useState(0);

  const additionalContext = useMemo(() => ({ settingsTenantLocation }), [settingsTenantLocation]);

  useEffect(() => {
    const ul = listRef.current;
    if (!ul || events.length <= 1) {
      setLineHeight(0);
      return;
    }

    const calculateHeight = () => {
      const lastLi = ul.children[ul.children.length - 1] as HTMLElement | null;
      if (!lastLi) {
        setLineHeight(0);
        return;
      }
      // Line spans from top of first marker to center of last marker.
      // First marker top is at TIME_ROW_HEIGHT from the ul top.
      // Last marker center is at lastLi.offsetTop + TIME_ROW_HEIGHT + MARKER_SIZE / 2.
      setLineHeight(lastLi.offsetTop + MARKER_SIZE / 2);
    };

    calculateHeight();

    // Recalculate when content resizes (e.g. expandable event cards).
    const observer = new ResizeObserver(calculateHeight);
    observer.observe(ul);

    return () => observer.disconnect();
  }, [events]);

  useEffect(() => {
    if (!activeEventId || !activeRef.current) {
      setIsPositioned(true);
      return;
    }
    // Use requestAnimationFrame to ensure the DOM has laid out before scrolling.
    // Instantly scroll (no animation) so the list appears already at the right spot,
    // then reveal the content.
    const rafId = requestAnimationFrame(() => {
      activeRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      setIsPositioned(true);
    });
    return () => cancelAnimationFrame(rafId);
  }, [activeEventId, events]);

  const shouldShowLine = events.length > 1 && lineHeight > 0;

  return (
    <ul ref={listRef} css={listStyle(isPositioned, shouldShowLine, lineHeight)}>
      {events.map((event, index) => {
        const isActive = !!activeEventId && event.id === activeEventId;
        return (
          <EventListItem
            key={event.id ?? index}
            ref={isActive ? activeRef : undefined}
            event={event}
            isHighlighted={isActive}
            additionalContext={additionalContext}
          />
        );
      })}
    </ul>
  );
};

// --- Styles ---

const listStyle = (isPositioned: boolean, shouldShowLine: boolean, lineHeight: number) => css`
  list-style: none;
  margin: 0;
  padding: 0;
  padding-bottom: var(--fabric-space-gap-lg);
  display: flex;
  flex-direction: column;
  position: relative;
  gap: var(--fabric-space-gap-lg);
  opacity: ${isPositioned ? 1 : 0};
  transition: opacity 0.15s ease-in;

  /* Single continuous vertical timeline line */
  &::before {
    content: ${shouldShowLine ? "''" : 'none'};
    position: absolute;
    /* Horizontally centered on the marker column (half of MARKER_SIZE) */
    left: ${MARKER_SIZE / 2}px;
    transform: translateX(-50%);
    /* Start at the top of the first marker */
    top: ${TIME_ROW_HEIGHT}px;
    /* Height is dynamically measured to end at the center of the last marker */
    height: ${lineHeight}px;
    width: 1px;
    background-color: var(--fabric-color-border-default);
  }
`;
